import h from 'hyperscript';
import isEqual from 'lodash-es/isEqual';

import { createSlider } from 'ui/slider/slider';

import {
  CHANGED,
  CONDITIONS_HIERARCHIES_CHANGE,
  FILTERS_APPLIED
} from 'cadenza/conditions/event-types';
import { on, unByKey } from 'cadenza/utils/event-util';
import { logger } from 'cadenza/utils/logging';
import { hashCode } from 'cadenza/utils/hash-code';
import { isFeatureAvailable } from 'cadenza/features';
import { CONDITION_SETTINGS } from 'cadenza/workbook/condition-edit-dialog/condition-settings';
import { TableCondition } from 'cadenza/conditions/table-condition/table-condition';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import {
  TableSliderAnimationSettingsDialog
} from 'cadenza/conditions/table-slider-condition/table-slider-animation-settings-dialog';

import './table-slider-condition.css';

const CONTEXT_SENSITIVE = 'context-sensitive';
const COMPONENT_NAME = 'd-table-slider-condition';

export class TableSliderCondition extends TableCondition {

  #conditionsApplied = true;
  #animateButton;
  #data;
  #affectedMapViews;
  _timeSliderApiSupplier;

  constructor ({
    columnsConfigSupplier, conditionApiSupplier, sortDescription, values, allowExcludeValues, excludeValues, maxSelection, settings,
    affectedMapViews = [],
    timeSliderApiSupplier = (layerId, conditionId) => import(/* webpackChunkName: "classic-time-slider-api" */'cadenza/api-client/timeslider/classic-time-slider-api')
      .then(({ ClassicTimeSliderApi }) => new ClassicTimeSliderApi(layerId, conditionId))
  } = {}) {
    super({ columnsConfigSupplier, conditionApiSupplier, sortDescription, values, allowExcludeValues, excludeValues, maxSelection });
    this.#affectedMapViews = affectedMapViews;
    this._timeSliderApiSupplier = timeSliderApiSupplier;
    this._settings = settings;
  }

  connectedCallback () {
    super.connectedCallback();
    if (this.multiple) {
      logger.error('Multiple selected values are not allowed for table slider condition.');
      return;
    }

    this._sliderContentBox = this._createSliderContentBox();
    this.append(this._sliderContentBox);

    const conditionConfig = this.previousElementSibling?.value;
    if (conditionConfig) {
      this.#data = conditionConfig?.allowedValues || [];
      this._subscribeToFilterController();
      this.createSlider();
    }
    this.classList.add(COMPONENT_NAME);
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  _subscribeToFilterController () {
    const filterController = this.filterController;
    this._eventKeys = [
      on(filterController, CONDITIONS_HIERARCHIES_CHANGE, (event) => this._filterCriteriaChanged(event)),
      on(filterController, CHANGED, (event) => this._filterChanged(event))
    ];
  }

  _filterChanged (event) {
    // This event will be raised when the changes of filters are applied so
    // the component can animate the result
    this._setStateToConditionsAlreadyApplied();
  }

  _createSliderContentBox () {
    this._sliderDiv = h('.table-condition-slider.noUi-horizontal');
    this._animateButtonContainer = h('.animate-button-container');
    return h('.table-condition-slider-box', [ this._sliderDiv, this._animateButtonContainer ]);
  }

  _canPlayAnimation () {
    const data = this.previousElementSibling?.value;
    return (data?.canPlayMapAnimation ?? isFeatureAvailable('PLAY_MAP_ANIMATION')) // has System Privilege
      && (this.animationAllowed || getSetting(this._settings, CONDITION_SETTINGS.CONDITION_ANIMATION_ALLOW, false)) // animation is allowed for this condition
      && this.isMapDisplayed(this.#affectedMapViews); // there is at least 1 map view affected by this condition
  }

  createSlider () {
    const data = this.#data;
    this._conditionTableRows = this.prepareRows(data);
    if (this._conditionTableRows.length < 2) {
      this._sliderContentBox.hidden = true;
      logger.info('Table slider condition should have at least 2 rows so it will be hidden.');
      return;
    }
    this._sliderContentBox.hidden = false;
    const selectedRowId = this.value.length > 0 ? this.value[0].id : null;
    this._selectedRowIndex = this._conditionTableRows.findIndex(row => row.id === selectedRowId);

    const formatFunction = rowIndex => this._conditionTableRows[Math.round(rowIndex)].text;
    const isUnset = !(this._selectedRowIndex >= 0);
    const start = isUnset ? 0 : this._selectedRowIndex;

    this._slider = createSlider(
      this._sliderDiv,
      {
        animationDuration: 100,
        range: { min: 0, max: this._conditionTableRows.length - 1 },
        start: [ start ],
        step: 1,
        keyboardSupport: true
      },
      {
        isUnset,
        simplePips: true,
        formatFunction
      });

    this._slider.on('change', values => {
      this._selectedRowIndex = Math.round(values[0]);
      this.value = [ this.selectedRow ];
      if (this.#animateButton) {
        this.#animateButton.disabled = this.selectedRow === undefined;
      }
      this.onConditionChange();
    });

    this._slider.on('slide', values => {
      this._selectedRowIndex = Math.round(values[0]);
      this.value = [ this.selectedRow ];
    });

    while (this._animateButtonContainer.firstChild) {
      this._animateButtonContainer.removeChild(this._animateButtonContainer.firstChild);
    }
    if (this._canPlayAnimation()) {
      Promise.all([
        import(/* webpackChunkName: "animate-action" */'cadenza/conditions/actions/animate-action'),
        // arguments will only be used by classic
        this._timeSliderApiSupplier(this.filterController?.layerId, this.conditionName)
      ]).then(([ { AnimateAction }, timeSliderApi ]) => {
        this.#animateButton = AnimateAction.createAnimateButton(
          this.#affectedMapViews,
          timeSliderApi,
          (defaultMaxFrames) => new TableSliderAnimationSettingsDialog({
            isWorkbook: this.#affectedMapViews,
            affectedViews: this.#affectedMapViews,
            rangeStart: this._conditionTableRows[start],
            rangeEnd: this._conditionTableRows[this._conditionTableRows.length - 1],
            selectedStart: this.selectedRow,
            maxFrames: this.maxFrames || defaultMaxFrames,
            conditionApi: this.conditionApi,
            tableColumnsDef: this._tableColumnsDef
          }),
          {
            preExecute: this._waitForLayerConditionsSet.bind(this)
          });
        this.#animateButton.disabled = this.selectedRow === undefined && this.defaultValue.length === 0;
        this._animateButtonContainer.append(this.#animateButton);
      })
        .catch(error => {
          logger.error('Failed to import module animate-action.', error);
        });
    }
  }

  _waitForLayerConditionsSet () {
    if (this.#conditionsApplied) {
      return Promise.resolve();
    }
    // The filters must be applied and when the layer conditions are set, the animation
    // can start safely
    return new Promise((resolve) => {
      on(this.filterController, FILTERS_APPLIED, resolve, { once: true });
      this.filterController.notifyApplyFilters();
    });
  }

  get contextSensitive () {
    return this.getAttribute(CONTEXT_SENSITIVE) !== 'false';
  }

  _filterCriteriaChanged (event) {
    this._setStateToConditionsNotAppliedYet();
    if (this.contextSensitive) {
      const contextSensitiveData = event.detail.eagerlyContextSensitiveData?.[this.persistenceId];
      if (contextSensitiveData) {
        const allowedValues = JSON.parse(contextSensitiveData.allowedValues);
        const newData = this.prepareRows(allowedValues);
        this._onContextSensitiveChange(newData);
      }
    }
  }

  _onContextSensitiveChange (newData) {
    if (isEqual(newData, this.#data)) {
      return;
    }
    this.#data = newData;
    if (this._selectedRowIndex >= 0) {
      const searchedId = this._conditionTableRows[this._selectedRowIndex].id;
      const newIndex = this.#data.findIndex(row => row.id === searchedId);
      this._selectedRowIndex = newIndex === -1 ? -1 : newIndex;
    }
    this._recreateSlider();
  }

  _recreateSlider () {
    if (this._slider) {
      this._slider.destroy();
    }
    this.createSlider();
  }

  get animationAllowed () {
    return (this.getAttribute('animation-allowed') === 'true');
  }

  get maxFrames () {
    return (this.getAttribute('max-frames')) || getSetting(this._settings, CONDITION_SETTINGS.CONDITION_ANIMATION_NUMBER_OF_FRAMES);
  }

  get selectedRow () {
    return this._selectedRowIndex >= 0 ? this._conditionTableRows[this._selectedRowIndex] : undefined;
  }

  set selectedRow (selectedRow) {
    if (!this._slider) {
      return;
    }
    if (!selectedRow) {
      this._recreateSlider();
      return;
    }
    const selectedIndex = this._conditionTableRows.findIndex(row => row.id === selectedRow.id);
    const currentSliderIndex = Math.round(this._slider.get());
    if (selectedIndex === -1 || selectedIndex === currentSliderIndex) {
      return;
    }
    this._selectedRowIndex = selectedIndex;
    this._slider.set(selectedIndex);
  }

  get contextSensitiveValues () {
    return this.#data;
  }

  set contextSensitiveValues (values) {
    const newData = prepareRowsForTableSlider(values?.rows);
    this._onContextSensitiveChange(newData);
  }

  onSelectionChange () {
    this.selectedRow = this.value[0];
  }

  _setStateToConditionsNotAppliedYet () {
    this._setStateToConditionsApplied(false);
  }

  _setStateToConditionsAlreadyApplied () {
    this._setStateToConditionsApplied(true);
  }

  _setStateToConditionsApplied (conditionsApplied) {
    this.#conditionsApplied = conditionsApplied;
  }

}

customElements.define(COMPONENT_NAME, TableSliderCondition);

function prepareRowsForTableSlider (rows = []) {
  return rows.map(row => ({
    text: row.formatted[0],
    id: String(hashCode(JSON.stringify(row.values))),
    ...row
  }));
}
